import { memo, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Button, Modal, notification } from 'antd';
import PopupForm from '../Form/PopupForm';
import { Props } from './types';
import styles from './style.module.css';
import { useAddUserPopup } from '../../../hooks/features/useAddUserPopup/useAddUserPopup';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { selectRoles } from '../../../store/reducers/RoleSlice';
import { selectClinics } from '../../../store/reducers/ClinicSlice';
import { getUsers } from '../../../store/reducers/UsersSlice';
import {
  APP_PASSWORD_INVALID_MESSAGE,
  APP_USERNAME_INVALID_MESSAGE,
  isValidAppPassword,
  isValidAppUsername,
} from '../../../components/shared/AppPasswordInput/credentialPolicy';

export type ErrorState = {
  userName?: ErrorData;
  password?: ErrorData;
  role?: ErrorData;
  clinic?: ErrorData;
};

export type ErrorData = {
  validateStatus: '' | 'error' | 'success' | 'warning' | 'validating';
  help: string;
};

const AddUserPopup = memo(({ onCancel, open, extra }: Props) => {
  const formId = `add-user-${useId().replace(/:/g, '')}`;
  const dispatch = useAppDispatch();
  const roles = useAppSelector(selectRoles);
  const clinics = useAppSelector(selectClinics);
  const [submitting, setSubmitting] = useState(false);
  const reportData = useAddUserPopup({
    roles,
    selectedUser: extra?.selectedUser,
    isEditing: extra?.isEditing,
    open,
  });
  const [errors, setErrors] = useState<ErrorState>({});
  const [api, contextHolder] = notification.useNotification();

  const isAdmin = useMemo(() => {
    const selectedRoleName = roles.find(
      (r) => r.id === reportData.selectedRole,
    )?.name;
    return selectedRoleName === 'Admin';
  }, [roles, reportData]);

  useEffect(() => {
    if (open) {
      setErrors({});
    }
  }, [open]);

  const checkValid = useCallback(() => {
    let hasError = false;

    const newErrors = {} as ErrorState;

    if (!extra?.isEditing) {
      if (!reportData.userName) {
        newErrors.userName = {
          validateStatus: 'error',
          help: 'Укажите логин нового пользователя',
        };

        hasError = true;
      } else if (!isValidAppUsername(reportData.userName)) {
        newErrors.userName = {
          validateStatus: 'error',
          help: APP_USERNAME_INVALID_MESSAGE,
        };

        hasError = true;
      }

      if (!reportData.password) {
        newErrors.password = {
          validateStatus: 'error',
          help: 'Укажите пароль нового пользователя',
        };

        hasError = true;
      } else if (!isValidAppPassword(reportData.password)) {
        newErrors.password = {
          validateStatus: 'error',
          help: APP_PASSWORD_INVALID_MESSAGE,
        };

        hasError = true;
      }
    }

    if (!reportData.selectedRole) {
      newErrors.role = {
        validateStatus: 'error',
        help: 'Выберите роль пользователя',
      };

      hasError = true;
    }

    if (
      !isAdmin &&
      (!reportData.selectedClinics || !reportData.selectedClinics.length)
    ) {
      newErrors.clinic = {
        validateStatus: 'error',
        help: 'Выберите минимум одну медицинскую организацию',
      };

      hasError = true;
    }

    setErrors(newErrors);

    return !hasError;
  }, [
    extra?.isEditing,
    isAdmin,
    reportData.password,
    reportData.selectedClinics,
    reportData.selectedRole,
    reportData.userName,
  ]);

  const handleFocus = (field: 'userName' | 'password' | 'role' | 'clinic') => {
    const newErrors = { ...errors };
    delete newErrors[field];

    setErrors(newErrors);
  };

  const clearAndClose = useCallback(() => {
    reportData.handleReset();
    setErrors({});
    onCancel();
  }, [reportData, onCancel]);

  const handleMainButton = useCallback(async () => {
    if (submitting) return;
    if (!checkValid()) return;

    setSubmitting(true);
    let shouldClose = false;

    try {
      if (extra?.isEditing) {
        const result = await reportData.handleEditOk();
        if (result.type === 'Users/editUser/rejected') {
          api.open({
            message: 'Не удалось изменить запись пользователя!',
          });
          return;
        }

        if (extra?.callRequest) {
          dispatch(getUsers(extra.callRequest));
        }
      } else {
        const result = await reportData.handleOk();
        if (result.type === 'Users/addUser/rejected') {
          api.open({
            message: 'Не удалось создать пользователя!',
          });
          return;
        }

        if (extra?.callRequest) {
          dispatch(getUsers(extra.callRequest));
        }
      }

      shouldClose = true;
    } catch {
      api.open({
        message: extra?.isEditing
          ? 'Не удалось изменить запись пользователя!'
          : 'Не удалось создать пользователя!',
      });
    } finally {
      setSubmitting(false);
    }

    if (shouldClose) {
      clearAndClose();
    }
  }, [api, checkValid, clearAndClose, dispatch, extra, reportData, submitting]);

  return (
    <>
      {contextHolder}
      <Modal
        className={styles.modalContent}
        open={open}
        onCancel={submitting ? undefined : clearAndClose}
        closable={!submitting}
        maskClosable={!submitting}
        destroyOnHidden={true}
        footer={[
          <Button
            key="toForm"
            type="primary"
            htmlType={extra?.isEditing ? 'button' : 'submit'}
            form={extra?.isEditing ? undefined : formId}
            onClick={extra?.isEditing ? handleMainButton : undefined}
            loading={submitting}
            disabled={submitting}
          >
            {extra?.isEditing ? 'Сохранить' : 'Создать пользователя'}
          </Button>,
        ]}
        width={550}
      >
        <PopupForm
          key={extra?.selectedUser?.id || (open ? 'new' : 'closed')}
          formId={formId}
          onSubmit={extra?.isEditing ? undefined : handleMainButton}
          roles={roles}
          clinics={clinics}
          login={reportData.userName}
          onLoginChange={reportData.onUserNameChange}
          password={reportData.password}
          onPasswordChange={reportData.onPasswordChange}
          onPasswordGenerated={reportData.onPasswordGenerated}
          role={reportData.selectedRole}
          onRoleChange={reportData.onSelectedRoleChange}
          clinic={reportData.selectedClinics}
          onClinicChange={reportData.onSelectedClinicChange}
          disabled={reportData.isLockout}
          onEnabledChange={reportData.onIsLockoutChange}
          errors={errors}
          isAdmin={isAdmin}
          isEditing={extra?.isEditing}
          selectedUser={extra?.selectedUser}
          handleFocus={handleFocus}
          submitting={submitting}
        />
      </Modal>
    </>
  );
});

AddUserPopup.displayName = 'AddUserPopup';
export default AddUserPopup;
